import { AxiosResponse } from "axios";
import { toast } from "sonner";

export default function useHandleDownloadDocument() {
    const getFilenameFromContentDisposition = (contentDisposition?: string): string | null => {
        if (!contentDisposition) return null;

        // Prefer RFC 5987 / RFC 6266: filename*=UTF-8''encoded
        // Example: attachment; filename*=UTF-8''My%20File.pdf
        const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
        if (filenameStarMatch?.[1]) {
            const raw = filenameStarMatch[1].trim();
            const value = raw.replace(/^"(.*)"$/, '$1'); // strip quotes if any
            const parts = value.split("''");
            const encodedPart = parts.length >= 2 ? parts.slice(1).join("''") : value;
            try {
                return decodeURIComponent(encodedPart);
            } catch {
                return encodedPart;
            }
        }

        // Fallback: filename="file.pdf" or filename=file.pdf
        const filenameMatch = contentDisposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
        if (filenameMatch?.[2]) return filenameMatch[2].trim();

        return null;
    };

    const ensureFilenameWithExtension = (name: string, extension?: string): string => {
        const trimmed = (name || '').trim();
        if (!trimmed) return extension ? `document.${extension}` : 'document';
        if (trimmed.includes('.')) return trimmed;
        if (!extension) return trimmed;
        const ext = extension.startsWith('.') ? extension.slice(1) : extension;
        return `${trimmed}.${ext}`;
    };

    const guessMimeTypeFromExtension = (extension?: string): string => {
        const ext = (extension || '').toLowerCase().replace(/^\./, '');
        if (!ext) return 'application/octet-stream';
        if (ext === 'pdf') return 'application/pdf';
        if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || ext === 'webp') return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
        if (ext === 'txt') return 'text/plain';
        if (ext === 'doc') return 'application/msword';
        if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        if (ext === 'csv') return 'text/csv';
        return 'application/octet-stream';
    };

    const handleDownloadDocument = async (service: any, documentId: string, documentName: string, mimeType: string = 'application/octet-stream') => {
        try {
            const response = await service(documentId);

            if (response && response.getResponseData()) {
                const fileData = response.getResponseData();

                // Verificar que los datos no estén vacíos
                if (fileData) {
                    // Crear un blob con el tipo MIME proporcionado
                    const blob = new Blob([fileData], { type: mimeType });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', documentName);

                    // Descargar el archivo
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    toast.success('Documento descargado con éxito');
                } else {
                    toast.error('El documento está vacío o no se pudo obtener el archivo');
                }
            } else {
                toast.error('Error al descargar el documento');
            }
        } catch (error: any) {
            toast.error(`Error al descargar el documento: ${error.message}`);
        }
    };

    const handleDownloadDocumentNewTab = async (service: any, documentId: string, documentName: string, extension: string = 'pdf') => {
        try {
            const response = (await service(documentId)).getResponse() as AxiosResponse;

            if (response.status === 200) {
                const contentDisposition =
                    (response.headers?.['content-disposition'] as string | undefined) ||
                    (response.headers?.['Content-Disposition'] as string | undefined);
                const contentType =
                    (response.headers?.['content-type'] as string | undefined) ||
                    (response.headers?.['Content-Type'] as string | undefined);

                const backendFilename = getFilenameFromContentDisposition(contentDisposition || undefined);
                const effectiveFilename = backendFilename || ensureFilenameWithExtension(documentName, extension);
                const effectiveExtension = (effectiveFilename.split('.').pop() || extension || 'pdf').toLowerCase();

                const mimeType = contentType || guessMimeTypeFromExtension(effectiveExtension);

                const blob = new Blob([response.data], { type: mimeType });
                const url = window.URL.createObjectURL(blob);

                if (mimeType.toLowerCase().includes('application/pdf') || effectiveExtension === 'pdf') {
                    window.open(url, '_blank');
                }

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', effectiveFilename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                toast.error('Error al descargar el documento');
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || 'Error al descargar el documento');
        }
    };

    return { handleDownloadDocument, handleDownloadDocumentNewTab };
}
